import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DEFAULT_LIMIT_OPTIONS = [12, 48, 96];

interface PaginationControlsProps {
  page: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
  onLimitChange: (limit: number) => void;
  limitOptions?: number[];
}

export default function PaginationControls({
  page,
  total,
  limit,
  onPageChange,
  onLimitChange,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
}: PaginationControlsProps) {
  const totalPages = Math.ceil(total / limit);
  const [inputPage, setInputPage] = useState(String(page + 1));

  if (totalPages <= 1) return null;

  const handlePageChange = () => {
    const newPage = Number(inputPage);
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage - 1);
    }
  };

  const handleInputChange = (value: string) => {
    setInputPage(value);
  };

  return (
    <div className="flex flex-row items-center justify-between gap-4 py-4">
      <div className="flex flex-row items-center gap-4">
        <Button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          variant="outline"
        >
          <span className="m-auto">Prev</span>
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm">Page</span>
          <Input
            aria-label="Go to page"
            value={inputPage}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePageChange();
              }
            }}
            className="w-16 text-center"
          />
          <span className="text-sm">of {totalPages}</span>
        </div>
        <Button
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          variant="outline"
        >
          <span className="m-auto">Next</span>
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm">Show:</span>
        <Select
          value={String(limit)}
          onValueChange={(value) => onLimitChange(Number(value))}
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {limitOptions.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
